import {TimelineActionTrackFragment, TimelineFragment, TimelineFragmentSaveData} from './TimelineFragment';
import TimelineObject from "./TimelineObject";
import {ObjectTriggerTypes} from "../../interfaces/enums";
import store, {StoreActions} from "../../state/store";
import {IReversibleTimelineElement, ITimelineElementParent, TimelineElementTypes} from "../../interfaces/Timeline";
import {TimelineEffectorTypes, TimelineTrackTypes} from '../../globals';
import { getUuidv4 } from '../utilities/Misc';
import {EffectorMap, timelineElementMap} from ".";

export interface TimelineChannelData
{
	channelId: string;
	name: string;
}

export interface TimelineChannelSaveData
{
	id: string;
	objectId: string;
	name: string;
	fragments?: TimelineFragmentSaveData[];
}

const timelineChannelDataObject: TimelineChannelData = {
	channelId: "",
	name: "",
};

export default class TimelineChannel implements IReversibleTimelineElement, ITimelineElementParent
{
	#id: string;
	#object: TimelineObject;
	fragments: TimelineFragment[];
	uiInfo: any;

	constructor(name: string, object: TimelineObject)
	{
		this.#id = getUuidv4();
		this.#object = object;

		const dataObject = {
			...timelineChannelDataObject,
			channelId: this.id,
			name: name,
		};
		store.dispatch(StoreActions.timeline.setElementData({ dataId: this.id, data: dataObject }));
		this.fragments = [];

		this.uiInfo = {
			isSelected: false,
			isRenaming: false,
		};

		timelineElementMap.set(this.id, this);
	}

	getDataObject()
	{
		return store.getState().timelineModule.elementData[this.id];
	}

	get id()
	{
		return this.#id;
	}

	get object()
	{
		return this.#object;
	}

	get name()
	{
		return this.getDataObject().name;
	}

	set name(name: string)
	{
		store.dispatch(StoreActions.timeline.setElementProperty({ elementId: this.id, propertyName: "name", propertyValue: name }));
	}

	clone(newObject: TimelineObject): TimelineChannel
	{
		// make new channel
		const newChannel = new TimelineChannel(this.name, newObject);

		// clone parameters
		newChannel.uiInfo = Object.assign({}, this.uiInfo);

		// clone fragments
		for (const oldFragment of this.fragments)
		{
			newChannel.fragments.push(oldFragment.clone(newChannel));
		}

		return newChannel;
	}

	save(shouldIncludeChildren: boolean): TimelineChannelSaveData
	{
		const output: TimelineChannelSaveData = {
			id: this.id,
			objectId: this.object.id,
			name: this.name,
			fragments: undefined
		};

		if (shouldIncludeChildren)
		{
			output.fragments = [];
			this.fragments.forEach((f) => output.fragments?.push(f.save()));
		}

		return output;
	}

	load(savedData: TimelineChannelSaveData)
	{
		store.dispatch(StoreActions.timeline.reparentElementData({ dataId: this.id, newDataId: savedData.id }));
		store.dispatch(StoreActions.timeline.setElementProperty({ elementId: savedData.id, propertyName: "channelId", propertyValue: savedData.id }));

		this.#id = savedData.id;
		timelineElementMap.set(this.id, this);

		this.name = savedData.name;

		if (savedData.fragments)
		{
			for (let f = 0; f < savedData.fragments.length; f++)
			{
				const savedFragmentData = savedData.fragments[f];
				if (!EffectorMap.has(savedFragmentData.effectorType as TimelineEffectorTypes)) continue;
				if (this.object.track.type === TimelineTrackTypes.ObjectsTrack && savedFragmentData.effectorType === TimelineEffectorTypes.EventInvocation &&
					(savedFragmentData.effector.target === ObjectTriggerTypes.MoveToStep || savedFragmentData.effector.target === ObjectTriggerTypes.Alignment))
				{
					//backwards compatibility fix - MoveToStep/Alignment events can no longer exist on an objectsTrack and must be moved to the actionsTrack
					const actionsTrackChannel = this.object.track.timeline.actionsTrack!.objects[0].channels[0];
					const loadedFragment = new TimelineActionTrackFragment(savedFragmentData.name, actionsTrackChannel, savedFragmentData.effectorType);
					loadedFragment.load(savedFragmentData);
					actionsTrackChannel.fragments.push(loadedFragment);
				}
				else
				{
					const loadedFragment = this.object.track.type === TimelineTrackTypes.ObjectsTrack ?
						new TimelineFragment(savedFragmentData.name, this, savedFragmentData.effectorType) :
						new TimelineActionTrackFragment(savedFragmentData.name, this, savedFragmentData.effectorType);
					loadedFragment.load(savedFragmentData);
					this.fragments.push(loadedFragment);
				}
			}
		}
	}

	export()
	{

	}

	getType()
	{
		return TimelineElementTypes.Channel;
	}

	getParent()
	{
		return this.object;
	}

	setParent(parent: ITimelineElementParent): void
	{
		this.#object = parent as TimelineObject;
	}

	getChildren()
	{
		return this.fragments;
	}

	setChildren(children: TimelineFragment[])
	{
		this.fragments = children;
		for (const child of children)
		{
			child.setParent(this);
		}
	}

	getName()
	{
		return this.name;
	}

	deleteSelf()
	{
		for (let i = this.fragments.length - 1; i >= 0; i--)
		{
			this.fragments[i].deleteSelf();
		}

		if (this.object)
		{
			const myIndex = this.object.channels.indexOf(this);
			if (myIndex > -1)
			{
				this.object.channels.splice(myIndex, 1);
			}
		}

		timelineElementMap.delete(this.id);
	}

	onDelete()
	{
		this.getChildren().forEach((child) => child.onDelete());
	}

	onAdded()
	{
		this.getChildren().forEach((child) => child.onAdded());
	}

	reverse()
	{
		this.fragments.forEach((fragment) =>
		{
			fragment.reverse(false);
		});
	}
}
